import { Stack, Typography } from '@mui/material';
import { BicycleIcon, KeyIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';

import Roof from '@/assets/icons/roof.svg?react';
import { StatNumber } from '@/components/common/StatNumber';
import { StyledDivider } from '@/components/common/StyledDivider';
import { CircleIconValue } from '@/components/map/details/CircleIconValue';
import { StationParkAndRideFacilityListItem } from '@/components/map/details/content/StationParkAndRideFacilityListItem';
import type { Station } from '@/modules/api/backend';
import { backendApiParkingDataFindAllLatestParkAndRideFacilityDataOptions } from '@/modules/api/backend/@tanstack/react-query.gen';

const ICON_SIZE = 20;

interface DetailCardStationContentProps {
  station: Station;
  doubleSpacing?: boolean;
}

export function StationDetailContent({ station, doubleSpacing }: DetailCardStationContentProps) {
  const { data: parkAndRideFacilitiesData } = useQuery({
    ...backendApiParkingDataFindAllLatestParkAndRideFacilityDataOptions(),

    select: (data) => data.filter((item) => item.facility.stationId === station.id),
  });

  return (
    <Fragment>
      <Stack alignItems="center" direction="row" gap={2} padding={2}>
        <Stack gap={0.5}>
          <Stack alignItems="center" direction="row" justifyContent="flex-start" spacing={1}>
            <BicycleIcon size={ICON_SIZE} />
            <StatNumber>{station.numBikeParkingStation ?? '?'}</StatNumber>
          </Stack>

          <Stack alignItems="center" direction="row" spacing={0.5}>
            <Typography
              title="Rad Stellplätze"
              sx={{
                display: 'inline',
                maxWidth: { xs: '100px', lg: '150px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Rad Stellplätze
            </Typography>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          direction="row"
          gap={{ xs: 1, lg: 0 }}
          sx={{
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
          }}
        >
          <CircleIconValue color="info" indicator={station.numBikeParkingCovered ?? '?'} label="Überdacht">
            <Roof />
          </CircleIconValue>
          <CircleIconValue color="info" indicator={station.numBikeReadyToRide ?? '?'} label="Leihräder">
            <KeyIcon size={ICON_SIZE} weight="bold" />
          </CircleIconValue>
        </Stack>
      </Stack>

      {station.parkAndRideFacilities && station.parkAndRideFacilities?.length > 0 ? (
        <Fragment>
          <StyledDivider sx={{ my: doubleSpacing ? 4 : 2, backgroundColor: 'text.primary' }} />
          <Stack py={0.5} spacing={doubleSpacing ? 4 : 2}>
            {station.parkAndRideFacilities.map((facility) => (
              <StationParkAndRideFacilityListItem
                key={facility.id}
                data={parkAndRideFacilitiesData?.find((item) => item.id === facility.id)}
                facility={facility}
              />
            ))}
          </Stack>
        </Fragment>
      ) : null}
    </Fragment>
  );
}
